<template>
  <div
    class="insights-unusual-activity-widget"
  >
    <div class="widget-wrapper">
      <div class="widget-common widget-small widget-full-height">
        <div class="widget-title d-flex align-items-baseline">
          Unusual activity instances
          <i
            v-if="tooltipContent"
            v-tooltip="tooltipOptions"
            class="hi hi-info-filled ml-5"
            :title="tooltipContent"
          />
          <div class="mr-10 flex-auto" />
          <InsightsUnusualActivityInvestigateNow
            v-if="totalIncidents"
            :mode="'simple'"
            :text="false"
            :organization-insights-url="organizationInsightsUrl"
            :user-id="userId"
            :query-date="queryDate"
            :start-week-on="startWeekOn"
            :time-zone="timeZone"
            class="insights-widget-view-more"
          />
        </div>
        <div
          class="widget-body"
          :class="bodyClasses"
        >
          <template v-if="!isFirstLoad">
            <div class="widget-details insights-unusual-activity-widget-content d-flex position-relative">
              <div class="insights-unusual-activity-widget-chart d-flex">
                <InsightsUnusualActivityChart
                  v-if="insightsAvailable"
                  :chart="chart"
                  :data="chartData"
                  :loading="isLoading"
                  :total-incidents="totalIncidents"
                  :expanded="false"
                />
                <img
                  v-else
                  :src="lowActivityImg.src"
                  :alt="lowActivityImg.alt"
                  width="72"
                  class="mr-10"
                />
                <AppChartLegend
                  v-if="totalIncidents && insightsAvailable"
                  :values="legendData"
                  has-tooltips
                  orientation="vertical"
                />
                <div
                  v-else
                  class="chart-legend"
                >
                  <p v-if="insightsAvailable">No unusual activity today</p>

                  <p v-if="!insightsAvailable && !monthlyTotal">No unusual activity detected</p>
                  <template v-if="monthlyTotal">
                    <p v-if="!insightsAvailable">This member has unusual activity.</p>
                    <div class="text mt-10">
                      <InsightsUnusualActivityInvestigateNow
                        :mode="'simple'"
                        :monthly-total="monthlyTotal"
                        :period="period"
                        :organization-insights-url="organizationInsightsUrl"
                        :query-date="queryDate"
                        :user-id="userId"
                        :start-week-on="startWeekOn"
                        :time-zone="timeZone"
                      />
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Activity > Screenshots > Unusual Activity Widget
 */
import { INSIGHTS, INSIGHTS_ASSETS } from '~/app_constants';
import { Insights, InsightsFilters, InsightsDateRanges } from '~/mixins';
import { mapActions, mapGetters, mapState } from 'vuex';
import { pluralize } from '~/utils/string_utils';

import AppChartLegend from '~/components/AppChartLegend.vue';
import InsightsUnusualActivityChart from '~/components/insights/InsightsUnusualActivityChart.vue';
import InsightsUnusualActivityInvestigateNow from '~/components/insights/InsightsUnusualActivityInvestigateNow.vue';
import { get, snakeCase, sortBy } from 'lodash-es';

const WIDGET_KEY = INSIGHTS.api.unusualActivity.team;

export default {
  name: 'InsightsUnusualActivityWidgetScreenshots',
  components: {
    AppChartLegend,
    InsightsUnusualActivityChart,
    InsightsUnusualActivityInvestigateNow,
  },
  mixins: [Insights, InsightsFilters, InsightsDateRanges],
  props: {
    timeZone: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    tooltipOptions: Object.freeze({ html: true, sanitize: false }),
    tooltipContent: INSIGHTS.tooltips.unusualActivity.content,
    limit: INSIGHTS.parameters.unusualActivityLimit,
    widgetKey: WIDGET_KEY,
    showDialog: {
      team: false,
      member: false,
    },
  }),
  computed: {
    ...mapState({
      organizationInsightsUrl: 'organization_insights_url',
      widgetData: (state) => get(state.widgets, WIDGET_KEY, {}),
      queryDate: (state) => get(state.query, 'from', {}),
    }),
    ...mapGetters([
      'insightsAvailable',
      'isUserDashboard',
      'currentDashboardUser',
      'currentDashboardId',
      'isIndividualTeamDashboard',
      'startWeekOn',
    ]),
    isLoading() {
      return !this.loadingWidgets || this.loadingWidgets[this.widgetKey] !== false;
    },
    sortOptions() {
      return this.widgetData?.sort || {};
    },
    hasTableData() {
      const key = this.isUserDashboard ? 'unusual_activities' : 'users';

      return this?.data?.[key]?.length;
    },
    monthlyTotal() {
      const stats = this.widgetData.data?.stats;

      if (!stats) {
        return 0;
      }

      return Object.values(stats).reduce((total, value) => total + parseInt(value || 0, 10), 0);
    },
    data() {
      if (this.isLoading) {
        return {};
      }

      if (this.period === 'requested') {
        // data for requested period
        return this.widgetData.data;
      }

      // data for this / last month
      const stats = this.widgetData.data?.stats;

      if (stats) {
        return {
          ...this.widgetData.data,
          stats: {},
        };
      }

      return {};
    },
    chartData() {
      if (this.isLoading) {
        return [];
      }
      const data = Object.entries(INSIGHTS.unusualActivity.labels).map(([key, label]) => {
        // Since the BE is returning number of instances, and we are looking for percentages in the charts
        // we need to calculate it with a simple rule of three: multiplying the instances by 100 and dividing by the total number.
        // Our aim is to get a percentage with one decimal, so we multiply and then divide by 10
        const value = Math.round(((this.chart[snakeCase(key)] * 100) / this.totalIncidents) * 10) / 10;

        return {
          key,
          label: label.text,
          value: value || 0,
          tooltip: this.formatTooltip(snakeCase(key), label.text),
        };
      });

      return sortBy(data, 'value');
    },
    userId() {
      return this.currentDashboardUser?.id;
    },
    teamId() {
      return this.filters?.team_id;
    },
    totalIncidents() {
      const stats = this.chart;

      return Object.values(stats).reduce((total, value) => total + parseInt(value || 0, 10), 0);
    },
    chart() {
      return (this.isRequestedDataPresent && this.data?.stats) || {};
    },
    users() {
      return this.data?.users || [];
    },
    memberDialogTitle() {
      return this.currentDashboardUser ? `${this.currentDashboardUser.name}'s unusual activity` : `Unusual activity`;
    },
    period() {
      return this.widgetData.data?.period;
    },
    placeholderLegendData() {
      return Object.entries(INSIGHTS.unusualActivity.labels).map(([key, label]) => {
        return {
          key,
          label: label.text,
          value: 0,
          tooltip: label.tooltip,
        };
      });
    },
    legendData() {
      if (this.isLoading) {
        return this.placeholderLegendData;
      }

      return this.chartData.map((label) => ({ ...label, tooltip: INSIGHTS.unusualActivity.labels[label.key].tooltip }));
    },
    lowActivityImg() {
      return INSIGHTS_ASSETS.no_time_tracked;
    },
    isRequestedDataPresent() {
      return this.period === 'requested' || !this.period;
    },
  },
  async created() {
    await this.fetchWidgets({
      widgetKey: this.widgetKey,
      extraQueryParams: {
        limit: this.limit,
        preview: true,
        time_zone: this.timeZone,
      },
    });
    this.isFirstLoad = false;
  },
  methods: {
    ...mapActions(['fetchWidgets']),
    toggleDialog() {
      const dialog = this.isUserDashboard ? 'member' : 'team';

      this.showDialog[dialog] = !this.showDialog[dialog];
    },
    async sort(options) {
      const params = {
        limit: this.limit,
        sort_by: options.field,
        sort_order: options.direction,
        time_zone: this.timeZone,
      };

      await this.fetchWidgets({
        widgetKey: this.widgetKey,
        extraQueryParams: params,
      });
    },
    switchMember(id) {
      const string = `${id}_user_id`;

      this.setPreviousView({ previousView: this.selectedFilter, currentView: string });
      this.setSelectedFilter(string);
      this.setMembersFilter(string);
    },
    formatTooltip(key, label) {
      const instances = this.chart[key] || 0;

      if (this.isUserDashboard) {
        return `<div>${instances} ${pluralize(instances, 'instance')}</div>`;
      }

      const members = this.users.filter((user) => user?.stats?.[key]).length || 0;

      return [
        '<div class="d-flex flex-column">',
        `<div>${instances} ${pluralize(instances, 'instance')}</div>`,
        `<div>${label}</div>`,
        members ? `<div>${members} ${pluralize(members, 'member')}</div>` : '',
        '</div>',
      ].join('');
    },
  },
};
</script>
